/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { OperationStatus } from "Constants/OperationStatus";
import { IAccessControlUser } from "Interfaces/AccessControl/IAccessControlUser";
import { IUserRole } from "Interfaces/AccessControl/IUserRole";
import { IFilterValue } from "Interfaces/IFilterValue";

import { IAccessControlState } from "./IAccessControlState";

/**
 * Returns current value from state for: users
 * @param accessControlState
 */
export const getUsers = (accessControlState: IAccessControlState): IAccessControlUser[] =>
  accessControlState.users;

/**
 * Returns current value from state for: eventsFilterValues
 * @param userProfileState
 */
export const getAccessControlFilterValues = (
  accessControlState: IAccessControlState
): IFilterValue[] => accessControlState.accessControlFilterValues;

/**
 * Returns current value from state for: currentPage
 * @param accessControlState
 */
export const getCurrentPage = (accessControlState: IAccessControlState): number =>
  accessControlState.currentPage;

/**
 * Returns current value from state for: pageSize
 * @param accessControlState
 */
export const getPageSize = (accessControlState: IAccessControlState): number =>
  accessControlState.pageSize;

/**
 * Returns current value from state for: searchText
 * @param accessControlState
 */
export const getSearchText = (accessControlState: IAccessControlState): string =>
  accessControlState.searchText;

/**
 * Returns the filtered count.
 * @param accessControlState
 */
export const getFilteredCount = (accessControlState: IAccessControlState): number =>
  accessControlState.filteredCount;

/**
 * Returns selected user profile.
 * @param userProfileState
 */
export const getSelectedUser = (
  accessControlState: IAccessControlState
): IAccessControlUser | undefined => accessControlState.selectedUser;

/**
 * Returns selected user profile.
 * @param userProfileState
 */
export const getSelectedUserId = (accessControlState: IAccessControlState): number | undefined =>
  accessControlState.selectedUserId;

/**
 * Returns new user id.
 * @param userProfileState
 */
export const getNewUserId = (accessControlState: IAccessControlState): number | undefined =>
  accessControlState.newUserId;

/**
 * Returns the user roles.
 * @param accessControlState
 */
export const getUserRoles = (accessControlState: IAccessControlState): IUserRole[] =>
  accessControlState.userRoles;

/**
 * Returns the network operation status.
 * @type {OperationStatus}
 */
export const getNetworkOperationStatus = (
  accessControlState: IAccessControlState
): OperationStatus => accessControlState.networkOperationStatus;

/**
 * Returns the network operation error.
 * @returns {OperationStatus}
 */
export const getNetworkOperationError = (
  accessControlState: IAccessControlState
): string | undefined => accessControlState.networkOperationError;

/**
 * Returns the current tenant.
 * @returns {string}
 */
export const getCurrentTenant = (accessControlState: IAccessControlState): string | undefined =>
  accessControlState.tenant;
